import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Wordmark } from '../brand/Wordmark';
import { NewTabHint } from '../ui/NewTabHint';
import { brandDescriptor } from '../../data/brandAssets';
import { externalLinks, primaryNav, allRoutes } from '../../data/site';
function desktopLinkClass({
  isActive
}: {
  isActive: boolean;
}) {
  return `relative py-2 font-display text-[15px] font-semibold text-brand-navy transition-colors hover:text-[#083a7c] ${isActive ? 'after:absolute after:-bottom-0.5 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-brand-yellow after:content-[""]' : ''}`;
}
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  return <header className="sticky top-0 z-40 w-full border-b-2 border-brand-navy bg-brand-tan" data-id="element-251" data-mp="3p0i4xzrixi28">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8" data-id="element-252" data-mp="3tzv1jc8763i0">
        {/* Official supplied wordmark plus a plain readable descriptor. */}
        <Link to="/" aria-label={`UCF BCM — ${brandDescriptor} — home`} className="flex shrink-0 items-center gap-3 py-1 pr-1" data-id="element-253" data-mp="19afgyyzknb0t">
          <Wordmark className="h-7 sm:h-8" data-id="element-254" data-mp="1d9xsj45i69qz" />
          <span className="hidden max-w-[16ch] border-l-2 border-brand-navy/25 pl-3 font-meta text-[11px] font-medium leading-tight text-brand-navy md:block lg:max-w-none" data-id="element-255" data-mp="3abvf4ghojlou">
            {brandDescriptor}
          </span>
        </Link>

        {/* gap tightens at lg so the extra nav item does not wrap. */}
        <nav aria-label="Main" className="hidden items-center gap-4 lg:flex xl:gap-6" data-id="element-256" data-mp="3edqfnirsdv1k">
          {primaryNav.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={desktopLinkClass} data-id="element-257" data-mp="2qoxg7cbyh527">
              {item.label}
            </NavLink>)}
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-5 font-display text-[15px] font-semibold text-white transition-all duration-150 hover:-translate-y-0.5 hover:bg-[#083a7c]" data-id="element-258" data-mp="30eqtqivnru93">
            Get Connected
            <NewTabHint data-id="element-259" data-mp="igittdh6pha1" />
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden" data-id="element-260" data-mp="ieoc0817m5a6">
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="hidden min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-4 font-display text-sm font-semibold text-white sm:inline-flex" data-id="element-261" data-mp="n8ao9z3go75g">
            Get Connected
            <NewTabHint data-id="element-262" data-mp="122xepc0dptnx" />
          </a>
          <button type="button" onClick={() => setMobileOpen(true)} aria-expanded={mobileOpen} aria-controls="mobile-menu" className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy transition-colors hover:bg-white" data-id="element-263" data-mp="166mzdqdxnr3o">
            <MenuIcon aria-hidden="true" className="h-6 w-6" data-id="element-264" data-mp="3cyw68ngozb2i" />
            <span className="sr-only" data-id="element-265" data-mp="3hwxq3alxzef1">Open menu</span>
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-266" data-mp="116scf498gi3">
        {mobileOpen ? <motion.div id="mobile-menu" role="dialog" aria-modal="true" aria-label="Site menu" className="fixed inset-0 z-50 flex flex-col bg-brand-tan lg:hidden" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -12
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -12
      }} transition={{
        duration: reduceMotion ? 0.12 : 0.28,
        ease: [0.22, 0.61, 0.36, 1]
      }} data-id="element-267" data-mp="11zpqphbptnk">
            <div className="flex items-center justify-between border-b-2 border-brand-navy px-5 py-3" data-id="element-268" data-mp="ax1gfpr8z3s9">
              <Link to="/" aria-label="UCF BCM home" className="py-1" data-id="element-269" data-mp="5yq7ricouzbm">
                <Wordmark className="h-7" data-id="element-270" data-mp="2votv1eiwnx2v" />
              </Link>
              <button type="button" ref={closeRef} onClick={() => setMobileOpen(false)} className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy" data-id="element-271" data-mp="2vm7farlv2b9k">
                <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-272" data-mp="2liiwldhmmzy5" />
                <span className="sr-only" data-id="element-273" data-mp="2qiy59rqyrj9s">Close menu</span>
              </button>
            </div>
            <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-5 py-6" data-id="element-274" data-mp="2vb83rw659rno">
              <p className="font-meta text-[12px] font-medium uppercase tracking-[0.16em] text-brand-navy" data-id="element-275" data-mp="2zcta0yo6b7ow">
                {brandDescriptor}
              </p>
              <ul className="mt-4 space-y-1" data-id="element-276" data-mp="34cxv72010qlg">
                {allRoutes.map(route => <li key={route.to} data-id="element-277" data-mp="398d5xl2qm6dn">
                    <NavLink to={route.to} end={route.to === '/'} className={({
                isActive
              }) => `block rounded-2xl px-4 py-3.5 font-display text-xl font-bold text-brand-navy ${isActive ? 'bg-brand-blue' : 'hover:bg-white'}`} data-id="element-278" data-mp="3e9ke3l8sl7ku">
                      {route.label}
                    </NavLink>
                  </li>)}
              </ul>
              <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-full border-2 border-brand-navy bg-brand-navy px-6 font-display text-lg font-semibold text-white" data-id="element-279" data-mp="1lucf178u3egx">
                Get Connected
                <NewTabHint data-id="element-280" data-mp="24hxv0hd5tgts" />
              </a>
              <p className="mt-4 font-meta text-[13px] text-brand-navy" data-id="element-281" data-mp="24l3o07owt2us">
                Thursdays at 7:30 PM • UCF Student Union, 3rd Floor
              </p>
            </nav>
          </motion.div> : null}
      </AnimatePresence>
    </header>;
}